import React, { useState, useEffect } from 'react';
import { DocumentDuplicateIcon, StarIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Table } from '../Shared/Table';
import { Button } from '../Shared/Button';
import { Modal } from '../Shared/Modal';
import { WorkflowConfig } from './WorkflowConfig';
import { workflowService, Workflow } from '../../services/workflowService';
import { useToast } from '../UI/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import { canManageSystemWorkflows } from '../../utils/ownerCustomerAccess';
import { Menu } from '@headlessui/react';

const getCustomerId = (): string =>
  sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';

export const WorkflowList: React.FC = () => {
  const { user } = useAuth();
  const canEditSystemWorkflows = canManageSystemWorkflows(user);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [configReadOnly, setConfigReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const loadWorkflows = async () => {
    try {
      setIsLoading(true);
      const customerId = getCustomerId();
      const data = customerId
        ? await workflowService.getWorkflowsByCustomer(customerId)
        : await workflowService.getWorkflows();
      setWorkflows(data);
    } catch (error) {
      addToast('Erro ao carregar workflows', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const handleSave = async (workflowData: Omit<Workflow, 'id'>) => {
    try {
      if (selectedWorkflow) {
        await workflowService.updateWorkflow(selectedWorkflow.id, workflowData);
        addToast('Workflow atualizado com sucesso', 'success');
      } else {
        await workflowService.createWorkflow(workflowData);
        addToast('Workflow criado com sucesso', 'success');
      }

      setShowConfigModal(false);
      loadWorkflows();
    } catch (error) {
      addToast('Erro ao salvar workflow', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este workflow?')) {
      try {
        const outcome = await workflowService.deleteWorkflow(id);
        if (outcome.mode === 'queued') {
          addToast(outcome.message, 'success');
        } else {
          addToast('Workflow excluído com sucesso', 'success');
        }
        loadWorkflows();
      } catch (error) {
        addToast('Erro ao excluir workflow', 'error');
      }
    }
  };

  const handleSetDefault = async (row: Workflow) => {
    try {
      await workflowService.setDefault(row.id);
      addToast(`"${row.name}" definido como workflow padrão`, 'success');
      loadWorkflows();
    } catch {
      addToast('Erro ao definir workflow padrão', 'error');
    }
  };

  const handleDuplicate = async (row: Workflow) => {
    try {
      const customerId = getCustomerId();
      await workflowService.createWorkflow({
        name: `${row.name} (Cópia)`,
        description: row.description,
        customerId: customerId || row.customerId || undefined,
        isSystem: false,
        isDefault: false,
        isActive: true,
        steps: row.steps.map(s => ({
          ...s,
          id: '',
          workflowId: '',
          assignees: [],
        })),
      });
      addToast('Workflow duplicado com sucesso', 'success');
      loadWorkflows();
    } catch {
      addToast('Erro ao duplicar workflow', 'error');
    }
  };

  const columns = [
    { header: 'Nome', accessor: 'name' },
    { header: 'Descrição', accessor: 'description' },
    { header: 'Etapas', accessor: 'steps', cell: (row: Workflow) => row.steps.length },
    {
      header: 'Status',
      accessor: 'isDefault',
      cell: (row: Workflow) => (
        <div className="flex gap-1 flex-wrap">
          {row.isDefault && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              <StarIconSolid className="h-3 w-3" />
              Padrão
            </span>
          )}
          {row.isSystem && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Sistema</span>
          )}
          {!row.isActive && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Inativo</span>
          )}
        </div>
      ),
    },
    {
      header: 'Ações',
      accessor: 'actions',
      cell: (row: Workflow) => (
        <div className="flex items-center gap-2">
          {row.isSystem && !canEditSystemWorkflows ? (
            <Button
              variant="secondary"
              size="sm"
              title="Visualizar etapas"
              onClick={() => {
                setSelectedWorkflow(row);
                setConfigReadOnly(true);
                setShowConfigModal(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              title="Editar"
              onClick={() => {
                setSelectedWorkflow(row);
                setConfigReadOnly(false);
                setShowConfigModal(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Button>
          )}

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button as={Button} variant="secondary" size="sm" title="Mais ações">
              <EllipsisVerticalIcon className="h-4 w-4" />
            </Menu.Button>

            <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                {!row.isDefault && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => handleSetDefault(row)}
                        className={`${
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        } group flex w-full items-center px-4 py-2 text-sm`}
                      >
                        <StarIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                        Definir como padrão
                      </button>
                    )}
                  </Menu.Item>
                )}

                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleDuplicate(row)}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-2 text-sm`}
                    >
                      <DocumentDuplicateIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                      Duplicar
                    </button>
                  )}
                </Menu.Item>

                {!row.isSystem && (
                  <>
                    <div className="border-t border-gray-100" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => handleDelete(row.id)}
                          className={`${
                            active ? 'bg-red-50 text-red-700' : 'text-red-600'
                          } group flex w-full items-center px-4 py-2 text-sm`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-3 h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Excluir
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}
              </div>
            </Menu.Items>
          </Menu>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Workflows</h1>
        <Button
          variant="primary"
          onClick={() => {
            setSelectedWorkflow(null);
            setConfigReadOnly(false);
            setShowConfigModal(true);
          }}
        >
          Novo Workflow
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <Table data={workflows} columns={columns} />
      )}

      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        maxWidthClassName="sm:max-w-6xl"
        title={
          configReadOnly && selectedWorkflow
            ? 'Visualizar workflow'
            : selectedWorkflow
              ? 'Editar Workflow'
              : 'Novo Workflow'
        }
      >
        <WorkflowConfig
          workflow={selectedWorkflow}
          readOnly={configReadOnly}
          onSave={handleSave}
          onCancel={() => setShowConfigModal(false)}
        />
      </Modal>
    </div>
  );
};
